import { useSelector } from 'react-redux';

import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { fetchServiceabilityWithDetails } from '@/apiCalls/fetchServicing';
import { getQuickQuote } from '@/apiCalls/matchingEngine';
import { DisplayButtons } from '@/components/DisplayButtons';
import {
    EEmploymentStatus,
    EIncomeSource,
    ELoanUsage,
    selectBillsPaidFrequency,
    selectEmploymentStatusByIndex,
    selectLoanUsage,
    selectMortgageRent,
    selectOtherIncome,
    selectOtherIncomeSource,
    selectPaidAmount,
    selectPaidFrequency,
    setAnnualKms,
    setHecsDebt,
} from '@/context/borrower';
import {
    EDisplays,
    EModals,
    selectSkipExpenseScreen,
    setDisplay,
    setModal,
    setServiceabilityResult,
} from '@/context/display';
import { selectLenders } from '@/context/lender';
import { RootState, store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { selectIsLoggedIn } from '@/context/user';
import { useCopy } from '@/copy';
import { calculateHecsAndNetIncome } from '@/utils/getHecsDebt';

interface IIncomeButtonsProps {
    setShowErrors: (value: boolean) => void;
    baseId: string;
}

export function IncomeButtons({
    setShowErrors,
    baseId,
}: IIncomeButtonsProps): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const employment = useSelector((state: RootState) =>
        selectEmploymentStatusByIndex(state, 0)
    );
    const employmentStatus = employment?.status;
    const incomeSource = employment?.incomeSource;
    const paidFrequency = useAppSelector(selectPaidFrequency);
    const amountPaid = useAppSelector(selectPaidAmount);
    const otherIncome = useAppSelector(selectOtherIncome);
    const sourceOfOtherIncome = useAppSelector(selectOtherIncomeSource);
    const skipExpenseScreen = useAppSelector(selectSkipExpenseScreen);
    const rent = useAppSelector(selectMortgageRent);
    const billsPaidFrequency = useAppSelector(selectBillsPaidFrequency);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);

    // ***** Event Handlers *****
    function handleBackClick(): void {
        const backScreen = getBackScreen();
        dispatch(setDisplay(backScreen));
    }

    async function handleNextClick(): Promise<void> {
        setShowErrors(true);
        if (!isComplete()) return;
        const state = store.getState();
        const borrower = state.borrowerReducer;
        const amountPaidInBankAccountMonthly =
            state.borrowerReducer.income.paidAmount || 0;
        const income = state.borrowerReducer.income;
        const isHecsDebtDisplayed = (): boolean => {
            const income = state.borrowerReducer.income;
            if (!income) return false;

            let annualIncome = 0;
            // Convert paid amount to annual based on frequency
            if (income.paidAmount) {
                switch (income.paidFrequency) {
                    case 'WEEKLY':
                        annualIncome =
                            (income.paidAmount + (income.otherIncome || 0)) *
                            52;
                        break;
                    case 'FORTNIGHTLY':
                        annualIncome =
                            (income.paidAmount + (income.otherIncome || 0)) *
                            26;
                        break;
                    case 'MONTHLY':
                        annualIncome =
                            (income.paidAmount + (income.otherIncome || 0)) *
                            12;
                        break;
                    default:
                        annualIncome =
                            income.paidAmount + (income.otherIncome || 0);
                }
            }

            return annualIncome < 65000;
        };
        const isNovatedLease =
            state.borrowerReducer.loan.loanUsage === 'NOVATED';
        if (!isNovatedLease) {
            if (borrower.vehicle && borrower.vehicle.annualKms) {
                store.dispatch(setAnnualKms(undefined));
            }
            if (borrower.income && borrower.income.hecsDebt) {
                store.dispatch(setHecsDebt(undefined));
            }
        } else {
            if (
                borrower.income &&
                borrower.income.hecsDebt &&
                !isHecsDebtDisplayed()
            ) {
                store.dispatch(setHecsDebt(undefined));
            } else {
                const actualHecsRepayment = calculateHecsAndNetIncome(
                    amountPaidInBankAccountMonthly,
                    income.otherIncome || 0,
                    income.paidFrequency
                ).hecsRepayment;
                isHecsDebtDisplayed()
                    ? store.dispatch(setHecsDebt(actualHecsRepayment))
                    : store.dispatch(setHecsDebt(undefined));
            }
        }
        createOrUpdateBorrower();
        // Before getNextScreen, which runs serviceability and a quote: no
        // income is an answer on its own, and neither call can change it.
        // After createOrUpdateBorrower, so the lead is still saved.
        if (
            store.getState().borrowerReducer.employments.employments[0]
                ?.incomeSource === EIncomeSource.NO_INCOME
        ) {
            if (!isLoggedIn) {
                dispatch(setModal(EModals.REGISTER));
                return;
            }
            dispatch(setDisplay(EDisplays.NOINCOMEINELIGIBLE));
            return;
        }
        const nextScreen = await getNextScreen();
        await getQuickQuote();
        {
            const state = store.getState();
            const employmentStatus =
                state.borrowerReducer.employments.employments[0].status;
            if (
                employmentStatus === EEmploymentStatus.CASUAL &&
                loanUsage === ELoanUsage.NOVATED
            ) {
                if (!isLoggedIn) {
                    dispatch(setModal(EModals.REGISTER));
                    return;
                } else {
                    dispatch(setDisplay(EDisplays.EMPLOYMENTINELIGIBLE));
                    return;
                }
            }
            if (
                state.lenderReducer.lenders?.length === 0 &&
                loanUsage === ELoanUsage.NOVATED
            ) {
                if (!isLoggedIn) {
                    dispatch(setModal(EModals.REGISTER));
                } else {
                    if (employmentStatus === EEmploymentStatus.CASUAL) {
                        dispatch(setDisplay(EDisplays.EMPLOYMENTINELIGIBLE));
                        return;
                    }
                    dispatch(setDisplay(EDisplays.INCOMEINELIGIBLE));
                }
                return;
            } else {
                dispatch(setDisplay(nextScreen));
            }
        }
    }

    // ***** Helpers ******
    function isComplete(): boolean {
        const isNovatedLease = loanUsage === ELoanUsage.NOVATED;
        // The income source is the required answer. The kind of work is only
        // asked of someone who said Work, so requiring a status outright left
        // the other two sources unable to continue at all.
        if (!incomeSource) return false;
        if (incomeSource === EIncomeSource.WORK && !employmentStatus)
            return false;
        if (
            employmentStatus === EEmploymentStatus.CASUAL &&
            employment.years === undefined &&
            employment.months === undefined
        )
            return false;
        if (paidFrequency === undefined) return false;
        if (amountPaid === undefined) return false;
        if (
            otherIncome !== undefined &&
            otherIncome !== 0 &&
            sourceOfOtherIncome === undefined
        )
            return false;

        if (skipExpenseScreen && rent === undefined) return false;
        // The frequency beside the rent amount, which was marked required and
        // never checked: Next painted it red and then navigated anyway. It is
        // also what converts the amount for serviceability, so left unset a
        // fortnightly payment was read as monthly and the applicant looked
        // more affordable than they are.
        if (skipExpenseScreen && billsPaidFrequency === undefined) return false;
        return true;
    }
    const loanUsage = useAppSelector(selectLoanUsage);
    const { technicalFailureScreen } = useCopy();

    async function getNextScreen(): Promise<EDisplays> {
        if (!skipExpenseScreen) return EDisplays.EXPENSE;

        try {
            // 1. fetch serviceability result
            const serviceabilityResult = await fetchServiceabilityWithDetails();

            if (!serviceabilityResult.isServicing) {
                // 2. if not servicing, return failure
                return EDisplays.SERVICING_FAILURE;
            }

            // 3. if servicing, fetch lenders
            await getQuickQuote();

            // No wait here. getQuickQuote awaits the request and dispatches
            // the lenders before it resolves, so awaiting it is already
            // enough — the second this bought was spent on every customer
            // leaving this screen, and a slower quote would have run past it
            // anyway and been read as no lenders.

            // 5. get lenders result
            const state = store.getState();
            const lenders = selectLenders(state);
            const hasLenders = lenders && lenders.length > 0;

            // 6. store result in state
            dispatch(
                setServiceabilityResult({
                    isServicing: true,
                    hasLenders: hasLenders,
                    lenders: serviceabilityResult.lenders,
                })
            );

            // 7. return success
            return EDisplays.SERVICING_SUCCESS;
        } catch (error) {
            console.error('Serviceability check failed:', error);
            // The request never completed, so there is no affordability
            // answer. SERVICING_FAILURE tells the customer their income may
            // not be high enough, which is not something we know here.
            return technicalFailureScreen
                ? EDisplays.SERVICING_TECHNICAL_FAILURE
                : EDisplays.SERVICING_FAILURE;
        }
    }

    function getBackScreen(): EDisplays {
        return EDisplays.BASIC;
    }
    return (
        <DisplayButtons
            idLeftButton={`${baseId}-button-back`}
            idRightButton={`${baseId}-button-next`}
            rightClick={handleNextClick}
            leftClick={handleBackClick}
        />
    );
}
